import { useState } from 'react';
import { categories } from '../../../../mocks/categories';


export default function CourseBasicForm({ course, onChange, onThumbnailChange, 
  onInstructorAvatarChange }) {
  const [tagInput, setTagInput] = useState('');
  const [learnInput, setLearnInput] = useState('');
  const [reqInput, setReqInput] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [instructorAvatarFile, setInstructorAvatarFile] = useState(null);

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !course.tags.includes(tag)) {
      onChange({ tags: [...course.tags, tag] });
    }
    setTagInput('');
  };

  const addLearn = () => {
    const item = learnInput.trim();
    if (item) {
      onChange({ whatYouLearn: [...course.whatYouLearn, item] });
    }
    setLearnInput('');
  };

  const addReq = () => {
    const item = reqInput.trim();
    if (item) {
      onChange({ requirements: [...course.requirements, item] });
    }
    setReqInput('');
  };

  // Handle Thumbnail Upload
  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onThumbnailChange(file);                    // Pass file to parent
      const previewUrl = URL.createObjectURL(file);
      onChange({ thumbnail: previewUrl });        // Show preview
    }
  };

  // Handle Instructor Avatar Upload
  const handleInstructorAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onInstructorAvatarChange(file);             // Pass file to parent
      const previewUrl = URL.createObjectURL(file);
      onChange({ instructorAvatar: previewUrl });
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Course Information */}
          <div className="bg-white rounded-xl border border-slate-100 p-6 space-y-4">
            <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100">
              Course Information
            </h2>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Course Title *</label>
              <input
                type="text"
                value={course.title || ''}
                onChange={e => onChange({ title: e.target.value })}
                placeholder="e.g. Complete React & TypeScript Masterclass"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Short Description *</label>
              <textarea
                value={course.description || ''}
                onChange={e => onChange({ description: e.target.value })}
                placeholder="Brief description (shown in course cards)"
                rows={2}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 resize-none bg-white"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Long Description</label>
              <textarea
                value={course.longDescription || ''}
                onChange={e => onChange({ longDescription: e.target.value })}
                placeholder="Detailed course description (shown on course detail page)"
                rows={4}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 resize-none bg-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Category</label>
                <select
                  value={course.category || ''}
                  onChange={e => onChange({ category: e.target.value })}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Level</label>
                <select
                  value={course.level || 'Beginner'}
                  onChange={e => onChange({ level: e.target.value })}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Price ($)</label>
                <input
                  type="number"
                  value={course.price || 0}
                  onChange={e => onChange({ price: Number(e.target.value) })}
                  min={0}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Duration</label>
                <input
                  type="text"
                  value={course.duration || ''}
                  onChange={e => onChange({ duration: e.target.value })}
                  placeholder="e.g. 24 hours"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white"
                />
              </div>
            </div>
          </div>

          {/* Instructor Section */}
          <div className="bg-white rounded-xl border border-slate-100 p-6 space-y-4">
            <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100">Instructor</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Instructor Name</label>
                <input
                  type="text"
                  value={course.instructor || ''}
                  onChange={e => onChange({ instructor: e.target.value })}
                  placeholder="Full name"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Instructor Avatar</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleInstructorAvatarSelect}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                />
                {course.instructorAvatar && (
                  <div className="mt-2 w-16 h-16 rounded-lg overflow-hidden border">
                    <img src={course.instructorAvatar} alt="preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Bio</label>
              <textarea
                value={course.instructorBio || ''}
                onChange={e => onChange({ instructorBio: e.target.value })}
                placeholder="Short instructor biography"
                rows={2}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-teal-400 resize-none bg-white"
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100 mb-4">What You&apos;ll Learn</h2>
            <div className="space-y-2 mb-3">
              {course.whatYouLearn.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center text-teal-500 flex-shrink-0">
                    <i className="ri-check-line text-xs" />
                  </div>
                  <span className="text-sm text-slate-700 flex-1">{item}</span>
                  <button
                    onClick={() => onChange({ whatYouLearn: course.whatYouLearn.filter((_, i) => i !== idx) })}
                    className="w-5 h-5 flex items-center justify-center text-slate-300 hover:text-rose-400 cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={learnInput}
                onChange={e => setLearnInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addLearn()}
                placeholder="Add a learning objective..."
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400"
              />
              <button
                onClick={addLearn}
                className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm cursor-pointer hover:bg-teal-700 whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100 mb-4">Requirements</h2>
            <div className="space-y-2 mb-3">
              {course.requirements.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center text-slate-400 flex-shrink-0">
                    <i className="ri-circle-fill text-xs" />
                  </div>
                  <span className="text-sm text-slate-700 flex-1">{item}</span>
                  <button
                    onClick={() => onChange({ requirements: course.requirements.filter((_, i) => i !== idx) })}
                    className="w-5 h-5 flex items-center justify-center text-slate-300 hover:text-rose-400 cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={reqInput}
                onChange={e => setReqInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addReq()}
                placeholder="Add a requirement..."
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400"
              />
              <button
                onClick={addReq}
                className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm cursor-pointer hover:bg-teal-700 whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-5">
          {/* Thumbnail Upload */}
          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h2 className="text-sm font-bold text-slate-700 mb-3">Thumbnail</h2>
            
            {course.thumbnail && (
              <div className="mb-3 rounded-lg overflow-hidden h-40 border border-slate-200">
                <img 
                  src={course.thumbnail} 
                  alt="thumbnail preview" 
                  className="w-full h-full object-cover" 
                />
              </div>
            )}

            <label className="text-xs font-semibold text-slate-600 block mb-1.5">Upload Thumbnail (16:9 recommended)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailSelect}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
            />
            <p className="text-xs text-slate-500 mt-2">Max size: 5MB</p>
          </div>

          {/* Tags Section - unchanged */}
          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h2 className="text-sm font-bold text-slate-700 mb-3">Tags</h2>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {course.tags?.map(tag => (
                <span
                  key={tag}
                  className="flex items-center gap-1 text-xs bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full"
                >
                  {tag}
                  <button
                    onClick={() => onChange({ tags: course.tags.filter(t => t !== tag) })}
                    className="w-3 h-3 flex items-center justify-center hover:text-rose-500 cursor-pointer ml-0.5"
                  >
                    <i className="ri-close-line" style={{ fontSize: '10px' }} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTag()}
                placeholder="Add tag..."
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400"
              />
              <button
                onClick={addTag}
                className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm cursor-pointer hover:bg-teal-700 whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>

          <div className="bg-teal-50 border border-teal-100 rounded-xl p-4">
            <div className="flex items-start gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center text-teal-500 flex-shrink-0 mt-0.5">
                <i className="ri-information-line" />
              </div>
              <div>
                <p className="text-xs font-semibold text-teal-800">Tips</p>
                <ul className="text-xs text-teal-700 mt-1.5 space-y-1">
                  <li>&bull; Use clear, specific course titles</li>
                  <li>&bull; Add at least 4 learning objectives</li>
                  <li>&bull; Include prerequisite requirements</li>
                  <li>&bull; Upload a high-quality 16:9 thumbnail</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
